import {observer} from "mobx-react-lite";
import {useCallback, useContext, useEffect, useState} from "react";
import {BrowserRouter} from "react-router-dom";
import {CircularProgress} from "@mui/material";
import AppRouter from "./components/AppRouter";
import {check} from "./http/userAPI";
import Login from "./pages/Login";
import {Context} from ".";
import styles from "./App.css";
import {Overlay} from "./components/Overlay";
import {getAccess} from "./http/accessAPI";
import { setOnUnauthorized } from "./http/index";

const App = observer(() => {
    const {user, features, socket, chatStats, reviewRequestsStats} = useContext(Context);
    const [isLoading, setIsLoading] = useState(true);

    const handleStart = useCallback(async () => {
        try {
            if (localStorage.getItem("token")) {
                const userData = await check();
                userData.access = await getAccess(userData.id);
                user.setUser(userData);
                user.setIsAuth(true);
                // Загрузка данных о клинике
                await user.loadClinicData();
            } else {
                user.setIsAuth(false);
            }
        } catch (error) {
            console.error("Ошибка при проверке пользователя:", error);
            user.setIsAuth(false);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        handleStart();
    }, [handleStart]);

    useEffect(() => {
        if(user.isAuth){
           features.loadFeatures().catch(err => {
               alert(err);
           });
        }
    }, [user.isAuth, user.user.id, features])

    useEffect(() => {
        if (!user.isAuth || !user.user?.id) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        socket.connect(token);
        chatStats.loadClosedStats(user.user.id, null).catch((err) => {
            console.error("Ошибка загрузки глобальной статистики чатов:", err);
        });
        chatStats.loadOpenedStats(user.user.id, null).catch((err) => {
            console.error("Ошибка загрузки глобальной статистики открытых чатов:", err);
        });
        chatStats.loadMyStats(user.user.id, null).catch((err) => {
            console.error("Ошибка загрузки глобальной статистики моих чатов:", err);
        });
        reviewRequestsStats.loadPendingCount().catch((err) => {
            console.error("Ошибка загрузки количества необработанных отзывов:", err);
        });

        const onStatsUpdated = (data) => {
            chatStats.handleSocketStatsUpdated(data, user.user.id).catch((err) => {
                console.error("Ошибка обработки chat:statsUpdated:", err);
            });
        };
        const onReviewRequestUpdated = () => {
            reviewRequestsStats.handleSocketUpdated().catch((err) => {
                console.error("Ошибка обработки reviewRequest socket-события:", err);
            });
        };

        socket.on('chat:statsUpdated', onStatsUpdated);
        socket.on('pointsApplication:created', onReviewRequestUpdated);
        socket.on('pointsApplication:updated', onReviewRequestUpdated);

        return () => {
            socket.off('chat:statsUpdated', onStatsUpdated);
            socket.off('pointsApplication:created', onReviewRequestUpdated);
            socket.off('pointsApplication:updated', onReviewRequestUpdated);
        };
    }, [user.isAuth, user.user?.id, socket, chatStats, reviewRequestsStats]);

    useEffect(() => {
        if (user.isAuth) return;
        socket.disconnect();
        chatStats.reset();
        reviewRequestsStats.reset();
    }, [user.isAuth, socket, chatStats, reviewRequestsStats]);

    // Глобально реагируем на 401 из Axios интерсептора
    useEffect(() => {
        setOnUnauthorized(() => {
            user.setIsAuth(false);
        });
    }, [user]);

    if (isLoading) {
        return (
            <div className="loader-overlay">
                <CircularProgress className={styles.loader}/>
            </div>
        );
    }

    return (
        <BrowserRouter>
            {user.isAuth ? (
                <Overlay>
                    <AppRouter/>
                </Overlay>
            ) : (
                <Login/>
            )}
        </BrowserRouter>
    );
});

export default App;
